import React from 'react';
import { Box, Container, Typography, LinearProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BudgetCluesLogo from '../BudgetCluesLogo';

// Inputs stay at 16px on phones so iOS Safari doesn't auto-zoom on focus.
export const authInputSx = {
  borderRadius: 2.5,
  backgroundColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafafa'),
  '& .MuiInputBase-input': { fontSize: { xs: '1rem', md: '0.875rem' } },
};

export const authSubmitSx = {
  mt: 0.5,
  py: 1.05,
  borderRadius: 3,
  fontWeight: 700,
  textTransform: 'none',
  fontSize: '1rem',
  background: 'linear-gradient(90deg,#F41468,#ec407a)',
  '&:hover': { background: 'linear-gradient(90deg,#ec407a,#F41468)' },
};

const HERO_FEATURES = [
  'Auto-categorize transactions with rules',
  'Connect your bank securely via Akahu',
  'Budgets, forecasts and money-flow insights',
];

// Split-screen shell shared by the login, register and password-reset pages:
// brand hero on the left (hidden on phones), centered form column on the right.
const AuthLayout = ({ title, subtitle, busy = false, children }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      background: (t) =>
        t.palette.mode === 'dark'
          ? 'linear-gradient(135deg,#1a1c23,#272a33)'
          : 'linear-gradient(135deg,#fff,#f7f7fa)',
    }}
  >
    {/* Hero */}
    <Box
      sx={{
        flex: { xs: '0 0 auto', md: 1 },
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        justifyContent: 'flex-end',
        position: 'relative',
        background: 'linear-gradient(135deg,#d81b60 0%, #F41468 40%, #f06292 85%)',
        color: '#fff',
        py: 8,
        px: 10,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ maxWidth: 560, textAlign: 'left' }}>
        <Typography
          variant="h3"
          sx={{ fontWeight: 800, lineHeight: 1.04, mb: 2.5, letterSpacing: '-0.5px', textShadow: '0 6px 20px rgba(0,0,0,0.35)' }}
        >
          Your money leaves clues. We make them clear.
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400, lineHeight: 1.35 }}>
          Simple insights, smarter budgets, better decisions.
        </Typography>
        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {HERO_FEATURES.map((feature) => (
            <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 22, opacity: 0.9 }} />
              <Typography variant="body1" sx={{ fontWeight: 500, opacity: 0.95 }}>
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 25% 35%, rgba(255,255,255,0.25), transparent 60%)',
          pointerEvents: 'none',
        }}
      />
    </Box>

    {/* Form column */}
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: { xs: 4, md: 0 } }}>
      <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 520,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            px: { xs: 2.5, sm: 3 },
            py: { xs: 4, sm: 5 },
            mx: 'auto',
          }}
        >
          {busy && <LinearProgress sx={{ position: 'absolute', left: 0, top: 0, width: '100%' }} />}
          <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <BudgetCluesLogo size="medium" />
            {title && (
              <Typography variant="h5" sx={{ fontWeight: 500, mt: 1 }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {children}
        </Box>
      </Container>
    </Box>
  </Box>
);

export default AuthLayout;
